import React, { useState } from 'react';
import { getUILoc, getCardName, groupCards } from '../../../utils/cardUtils.js';
import { CardIllustration } from '../../Card.js';
import CardDetail from '../../CardDetail.js';

const OpportunityEvent = ({
  activeRun,
  cardDefs,
  executeOpportunityRemove,
  executeOpportunityDraw,
  startOpportunityBattle,
  onStartMatch,
  goToNextNodeSelection
}) => {
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedCardIds, setSelectedCardIds] = useState([]);
  const [drawGainedCardIds, setDrawGainedCardIds] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [inspectCard, setInspectCard] = useState(null);

  const getCardValue = (id) => {
    if (id === 1000 || id === 33) return 3; // 芸術 or マネー
    const def = cardDefs[id];
    if (!def) return 1;
    if (def.required_scale < 8) return 1;
    if (def.required_scale < 30) return 2;
    return 3;
  };

  const handleOpenRemoveModal = () => {
    setSelectedCardIds([]);
    setShowRemoveModal(true);
    setErrorMessage(null);
  };

  const toggleCardSelection = (id) => {
    setSelectedCardIds(prev => {
      if (prev.includes(id)) {
        const next = [...prev];
        const idx = next.lastIndexOf(id);
        next.splice(idx, 1);
        return next;
      } else {
        if (prev.length < 3) {
          return [...prev, id];
        } else {
          return [prev[1], prev[2], id]; // max 3, push out oldest
        }
      }
    });
  };

  const handleConfirmRemove = () => {
    if (selectedCardIds.length === 0) {
      setErrorMessage(getUILoc('YONOTSUNE.ERRORS.SELECT_1_3_CARDS') || '1〜3枚のカードを選択してください');
      return;
    }
    const res = executeOpportunityRemove(selectedCardIds);
    if (!res.success) {
      setErrorMessage(res.reason);
    } else {
      setShowRemoveModal(false);
    }
  };

  const handleChoiceDraw = () => {
    setErrorMessage(null);
    const res = executeOpportunityDraw();
    if (!res.success) {
      setErrorMessage(res.reason);
    } else if (res.gainedCardIds) {
      setDrawGainedCardIds(res.gainedCardIds);
    }
  };

  const handleConfirmDrawGained = () => {
    setDrawGainedCardIds(null);
    if (goToNextNodeSelection) goToNextNodeSelection();
  };

  const handleChoiceBattle = () => {
    setErrorMessage(null);
    const res = startOpportunityBattle();
    if (!res.success) {
      setErrorMessage(res.reason);
    } else {
      if (onStartMatch) onStartMatch();
    }
  };

  const groupedPlayerDeck = groupCards(activeRun.playerDeck);

  const renderRemoveModal = () => {
    if (!showRemoveModal) return null;

    const totalVal = selectedCardIds.reduce((sum, id) => sum + getCardValue(id), 0);

    return (
      <div className="player-details-overlay pile-detail-overlay" style={{ zIndex: 3000 }} onClick={() => setShowRemoveModal(false)}>
        <div className="player-details-modal pile-detail-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
          <div className="player-details-header">
            <h3>{getUILoc('YONOTSUNE.EVENTS.OPPORTUNITY.CHOICE_1_TITLE')}</h3>
            <button className="close-button" onClick={() => setShowRemoveModal(false)}>×</button>
          </div>
          <div className="player-details-content">
            <div style={{ background: 'rgba(0,0,0,0.3)', padding: '10px', marginBottom: '15px', borderRadius: '4px', border: '1px solid rgba(33, 150, 243, 0.3)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{getUILoc('YONOTSUNE.EVENTS.OPPORTUNITY.CHOICE_1_DESC')}</span>
                <span className="text-heal" style={{ fontWeight: 'bold' }}>獲得意識: +{totalVal}</span>
              </div>
            </div>

            <div className="compact-scroll-area">
              <div className="presented-grid">
                {groupedPlayerDeck.map((item, index) => {
                  const val = getCardValue(item.id);
                  const selectedCount = selectedCardIds.filter(id => id === item.id).length;
                  const isSelected = selectedCount > 0;

                  return (
                    <div
                      key={index}
                      className={`pile-card-group clickable ${isSelected ? 'active' : ''}`}
                      onClick={() => toggleCardSelection(item.id)}
                      style={{
                        border: isSelected ? '2px solid #4caf50' : '1px solid rgba(255,255,255,0.1)',
                        background: isSelected ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                        padding: '5px'
                      }}
                    >
                      <div className="pile-card-illustration-wrapper">
                        <CardIllustration cardId={item.id} isMini={true} />
                        <div className="pile-card-count-badge">×{item.count}</div>
                        {isSelected && <div className="pile-card-prob-badge" style={{ background: '#4caf50' }}>{selectedCount}</div>}
                        <div className="card-val" style={{ position: 'absolute', top: '2px', right: '2px', fontSize: '0.6rem', background: 'rgba(0,0,0,0.6)', padding: '1px 3px', borderRadius: '2px' }}>V:{val}</div>
                        <button className="inspect-btn-mini" style={{ position: 'absolute', bottom: '2px', right: '2px', zIndex: 5 }} onClick={(e) => { e.stopPropagation(); setInspectCard(cardDefs[item.id]); }}>🔍</button>
                      </div>
                      <div className="pile-card-name" style={{ fontSize: '0.7rem' }}>{getCardName(item.id)}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="action-row" style={{ marginTop: '20px' }}>
              <button
                className="yonotsune-btn green-btn"
                onClick={handleConfirmRemove}
                style={{ flex: 1 }}
              >
                {getUILoc('YONOTSUNE.EVENTS.OPPORTUNITY.CHOICE_1_BTN')} (意識+{totalVal})
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDrawGainedModal = () => {
    if (!drawGainedCardIds) return null;

    const groupedGained = groupCards(drawGainedCardIds);

    return (
      <div className="player-details-overlay pile-detail-overlay" style={{ zIndex: 3000 }}>
        <div className="player-details-modal pile-detail-modal" style={{ maxWidth: '500px' }}>
          <div className="player-details-header">
            <h3>{getUILoc('YONOTSUNE.EVENTS.OPPORTUNITY.CHOICE_2_TITLE')} - 獲得カード</h3>
          </div>
          <div className="player-details-content">
            <p style={{ marginBottom: '15px', fontSize: '0.9rem' }}>
              新たな知見として、以下のカードを獲得してデッキに追加しました：
            </p>
            <div className="presented-grid" style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
              {groupedGained.map((item, i) => (
                <div key={i} className="pile-card-group clickable" onClick={() => setInspectCard(cardDefs[item.id])}>
                  <div className="pile-card-illustration-wrapper">
                    <CardIllustration cardId={item.id} isMini={true} />
                    <div className="pile-card-count-badge">×{item.count}</div>
                  </div>
                  <div className="pile-card-name" style={{ fontSize: '0.8rem', marginTop: '5px' }}>{getCardName(item.id)}</div>
                </div>
              ))}
            </div>
            <div className="action-row">
              <button className="yonotsune-btn start-btn" onClick={handleConfirmDrawGained} style={{ width: '100%' }}>
                {getUILoc('YONOTSUNE.EVENTS.NEXT_NODE')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="event-resolver opportunity-event">
      <h2 style={{ fontSize: '1.2rem', marginBottom: '5px' }}>{getUILoc('YONOTSUNE.EVENTS.OPPORTUNITY.NAME')}</h2>
      <p className="resolver-intro" style={{ fontSize: '0.8rem', marginBottom: '15px' }}>
        {getUILoc('YONOTSUNE.EVENTS.OPPORTUNITY.INTRO')}
      </p>

      {errorMessage && (
        <div className="event-result-banner text-danger" style={{ padding: '8px 15px', marginBottom: '15px', background: 'rgba(244, 67, 54, 0.15)', border: '1px solid #f44336' }}>
          {errorMessage}
        </div>
      )}

      <div className="opportunity-choices" style={{ display: 'flex', flexDirection: 'column', gap: '15px', maxWidth: '650px', margin: '0 auto' }}>
        {/* Choice 1 */}
        <div className="choice-card" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '15px', textAlign: 'left' }}>
          <h3 style={{ margin: '0 0 5px 0', fontSize: '1rem', color: '#ffca28' }}>
            {getUILoc('YONOTSUNE.EVENTS.OPPORTUNITY.CHOICE_1_TITLE')}
          </h3>
          <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#ccc' }}>
            {getUILoc('YONOTSUNE.EVENTS.OPPORTUNITY.CHOICE_1_DESC')}
          </p>
          <button className="yonotsune-btn" onClick={handleOpenRemoveModal} style={{ width: '100%' }}>
            {getUILoc('YONOTSUNE.EVENTS.OPPORTUNITY.CHOICE_1_BTN')}
          </button>
        </div>

        {/* Choice 2 */}
        <div className="choice-card" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '15px', textAlign: 'left' }}>
          <h3 style={{ margin: '0 0 5px 0', fontSize: '1rem', color: '#4fc3f7' }}>
            {getUILoc('YONOTSUNE.EVENTS.OPPORTUNITY.CHOICE_2_TITLE')}
          </h3>
          <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#ccc' }}>
            {getUILoc('YONOTSUNE.EVENTS.OPPORTUNITY.CHOICE_2_DESC')}
          </p>
          <button className="yonotsune-btn" onClick={handleChoiceDraw} style={{ width: '100%' }}>
            {getUILoc('YONOTSUNE.EVENTS.OPPORTUNITY.CHOICE_2_BTN')}
          </button>
        </div>

        {/* Choice 3 */}
        <div className="choice-card" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '15px', textAlign: 'left' }}>
          <h3 style={{ margin: '0 0 5px 0', fontSize: '1rem', color: '#ef5350' }}>
            {getUILoc('YONOTSUNE.EVENTS.OPPORTUNITY.CHOICE_3_TITLE')}
          </h3>
          <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#ccc' }}>
            {getUILoc('YONOTSUNE.EVENTS.OPPORTUNITY.CHOICE_3_DESC')}
          </p>
          <button className="yonotsune-btn battle-btn" onClick={handleChoiceBattle} style={{ width: '100%' }}>
            {getUILoc('YONOTSUNE.EVENTS.OPPORTUNITY.CHOICE_3_BTN')}
          </button>
        </div>
      </div>

      {renderRemoveModal()}
      {renderDrawGainedModal()}

      {inspectCard && (
        <CardDetail
          card={inspectCard}
          cardDefs={cardDefs}
          onClose={() => setInspectCard(null)}
        />
      )}
    </div>
  );
};

export default OpportunityEvent;
